/**
 * Lilia Math editor — orchestrator.
 *
 * Tablet + desktop only. The drawer auto-folds under the equation
 * (block mode) at viewports < 980px; below ~720px we treat it as
 * "mobile-class" and short-circuit to a notice nudging the user to a
 * larger surface. No mobile UI is shipped.
 *
 * State: tokens + cursor + recent + drawer tab + slash anchor.
 * Keyboard layer is wired at the top level so typing anywhere on the
 * page inserts atoms (this matches the design handoff).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { EquationRenderer } from "./EquationRenderer";
import { SlashPalette } from "./SlashPalette";
import { CheatSheet } from "./CheatSheet";
import { SourceTab } from "./SourceTab";
import { StructuresTab } from "./StructuresTab";
import { SymbolsTab } from "./SymbolsTab";
import { CAT_COLORS, type SymbolEntry } from "./palette";
import {
  equationToLatex, getContainer, replaceContainer, T,
  type FocusPath, type StructToken, type Token, SAMPLE_EQUATIONS,
} from "./tokens";
import { loadCommonTheme, saveCommonTheme, type ThemeId } from "./commonThemes";
import { LibraryModal } from "./LibraryModal";
import { SaveToLibraryModal } from "./SaveToLibraryModal";
import { log } from "./logger";
import { DevToolsPanel, isDevToolsEnabled, type DevToolsState } from "./DevToolsPanel";
import { latexToTokens } from "./latexToTokens";

/**
 * Below this content width the editor stops trying to render the
 * side drawer + equation block side-by-side and stacks them. Lives
 * in CSS now (container query in the styled-jsx block below); kept
 * here as documentation so the rule is searchable.
 */
// const DRAWER_BREAKPOINT = 980; // see `@container embed (min-width: 980px)` in the style block
/** Mobile gate — below this viewport width we render a notice. */
const TOO_NARROW_BREAKPOINT = 720;

/**
 * Max snapshots kept on the math editor's undo/redo stacks (#71).
 * Mirrors editorStore.ts's UNDO_STACK_LIMIT for the block editor.
 */
const UNDO_STACK_LIMIT = 50;

/**
 * One point in the math editor's edit history. The "document" is the
 * flat token array plus the caret + which structure slot (if any) is
 * focused — restoring all three lands the user exactly where they were.
 */
interface MathSnapshot {
  tokens: Token[];
  cursor: number;
  /** Focus path of the active slot ([] = editing the root token list). */
  focusPath: FocusPath;
}

export type DrawerTab = "symbols" | "structures" | "source";

export interface MathEditorProps {
  /**
   * Where the editor is mounted. Drives the chrome contract.
   *
   * - `"embedded"` (default): the editor is inside a host modal/drawer
   *   (today: BlockEditorContainer for the Equation block). The host
   *   already owns the title bar + Save/Discard action bar, so the
   *   editor renders NO page header, NO breadcrumb, NO Library/Save/
   *   Done buttons, and NO pricing footer. Avoids the visual collision
   *   reported on 2026-05-31 (two "Library" buttons, two "Save" buttons,
   *   "Lilia Math" wrapped to two lines).
   * - `"standalone"`: the dedicated `/math` route (none exists today;
   *   kept as the contract for a future surface). Renders the full
   *   page header.
   *
   * Layout in both modes is driven by a CSS container query on the
   * editor body — keyed off the editor's own width, not
   * `window.innerWidth` — so a narrow modal on a wide monitor folds
   * the drawer below the equation block correctly.
   *
   * Reference: lilia-docs / design handoff "Math editor — Embedded mode" (2026-05-31).
   */
  mode?: "standalone" | "embedded";
  /** Initial tokens. Defaults to a sample equation. */
  initialTokens?: Token[];
  /** Initial cursor (defaults to end). */
  initialCursor?: number;
  /** Equation label (rendered in the header pill + LaTeX source). */
  label?: string;
  /** Breadcrumb / location text (e.g. "Topology I — § 3.4"). */
  breadcrumb?: string;
  /** Block index for the badge ("3.4.1"). */
  blockIndex?: string;
  /** Called whenever the equation changes. */
  onChange?: (tokens: Token[]) => void;
  /** Called when the user presses Done in the header. */
  onDone?: (latex: string) => void;
  /**
   * Optional explicit-save callback. When provided, the math editor
   * surfaces a Save button in the header and an Autosave dropdown.
   * Receives the current LaTeX; the host is expected to commit it
   * to the server (e.g. update the equation block and force a
   * document save). Resolves once the save round-trip completes so
   * the editor can timestamp `lastSavedAt`. Throw on failure.
   */
  onSaveToServer?: (latex: string) => Promise<void>;
  /** Show colour-by-category tinting on tokens. Default true. */
  blockColors?: boolean;
  /** Show the small "\cmd" caption under each tile. Default true. */
  showShortcuts?: boolean;
}

/** localStorage key for the user's autosave interval preference. */
const AUTOSAVE_KEY = "lilia.math.autosaveSeconds";
/** Allowed autosave intervals (seconds). 0 means off. */
const AUTOSAVE_OPTIONS: number[] = [0, 30, 60, 120, 300];

function loadAutosaveSeconds(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(AUTOSAVE_KEY);
    const n = raw === null ? 0 : Number(raw);
    return AUTOSAVE_OPTIONS.includes(n) ? n : 0;
  } catch {
    return 0;
  }
}

function saveAutosaveSeconds(n: number): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(AUTOSAVE_KEY, String(n)); } catch { /* */ }
}

export function MathEditor({
  mode = "embedded",
  initialTokens,
  initialCursor,
  label,
  breadcrumb,
  blockIndex,
  onChange,
  onDone,
  onSaveToServer,
  blockColors = true,
  showShortcuts = true,
}: MathEditorProps) {
  // DevTools panel is mounted only when the user flipped the flag.
  // Captured at mount so a mid-session toggle requires a reload —
  // which is fine, the panel is for sustained inspection sessions.
  const [devtoolsOn] = useState<boolean>(() => isDevToolsEnabled());
  useEffect(() => {
    log.info("init", "MathEditor mounted", {
      hasOnChange: !!onChange,
      hasOnDone: !!onDone,
      hasOnSaveToServer: !!onSaveToServer,
      initialTokenCount: initialTokens?.length ?? 0,
      label, breadcrumb,
    });
    return () => log.info("init", "MathEditor unmounted");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tokens, setTokens] = useState<Token[]>(initialTokens ?? SAMPLE_EQUATIONS.simple);
  const [cursor, setCursor] = useState<number>(initialCursor ?? (initialTokens ?? SAMPLE_EQUATIONS.simple).length);
  const [recent, setRecent] = useState<SymbolEntry[]>([]);
  const [flashKey, setFlashKey] = useState<number | null>(null);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>("symbols");
  const [slashOpen, setSlashOpen] = useState(false);
  // `?` cheat-sheet overlay. Ref mirrors state so the window keydown handler
  // (a stable listener with a stale closure) can bail while it's open.
  const [cheatOpen, setCheatOpen] = useState(false);
  const cheatOpenRef = useRef(false);
  cheatOpenRef.current = cheatOpen;
  /**
   * Active Common-panel theme. Persisted per-user in localStorage so
   * "which math am I writing this week" carries across documents.
   * First mount reads from storage; subsequent changes write back.
   */
  const [commonTheme, setCommonTheme] = useState<ThemeId>(() => loadCommonTheme());
  const onChangeCommonTheme = useCallback((t: ThemeId) => {
    log.info("state", `commonTheme → ${t}`);
    setCommonTheme(t);
    saveCommonTheme(t);
  }, []);

  /**
   * Focus path of the slot the caret is in. `[]` means the caret is in
   * the top-level token list; a non-empty path locates a nested slot
   * (e.g. a fraction's numerator, or a sqrt inside that numerator). The
   * `cursor` index is interpreted relative to whichever container this
   * path addresses, so insert / backspace / typing all route into the
   * focused slot. Cleared (to `[]`) by Escape out of the outermost slot.
   *
   * Declared BEFORE `handleLoadFromLibrary` so the callback's closure
   * captures a defined binding for `setFocusPath` at registration time
   * (avoids a temporal-dead-zone error on hot-reload).
   */
  const [focusPath, setFocusPath] = useState<FocusPath>([]);
  const [viewport, setViewport] = useState<number>(
    typeof window === "undefined" ? 1280 : window.innerWidth,
  );

  // ───── Undo / redo history (#71) ──────────────────────────────────
  // The math editor's "document" is tokens + cursor + slot focus. We
  // mirror the block editor's snapshot-stack pattern (editorStore.ts):
  // push a snapshot of the PRE-mutation state before every content
  // mutation, clear redo on a fresh edit, and cap each stack. Snapshots
  // read through refs (updated every render) so they always capture the
  // freshly-committed state regardless of callback-closure staleness.
  // Theme changes are a UI preference (which palette is shown), not
  // document content, so they are intentionally NOT on this stack.
  const latestTokensRef = useRef<Token[]>(tokens);
  latestTokensRef.current = tokens;
  const latestCursorRef = useRef<number>(cursor);
  latestCursorRef.current = cursor;
  const latestFocusPathRef = useRef<FocusPath>(focusPath);
  latestFocusPathRef.current = focusPath;

  const undoStackRef = useRef<MathSnapshot[]>([]);
  const redoStackRef = useRef<MathSnapshot[]>([]);

  const takeSnapshot = useCallback((): MathSnapshot => ({
    tokens: structuredClone(latestTokensRef.current),
    cursor: latestCursorRef.current,
    focusPath: latestFocusPathRef.current.map((s) => ({ ...s })),
  }), []);

  /**
   * Snapshot the current state onto the undo stack and clear redo.
   * Call at the START of any content mutation, before it is applied.
   */
  const pushHistory = useCallback(() => {
    undoStackRef.current = [...undoStackRef.current, takeSnapshot()].slice(-UNDO_STACK_LIMIT);
    redoStackRef.current = [];
  }, [takeSnapshot]);

  const applySnapshot = useCallback((s: MathSnapshot) => {
    setTokens(s.tokens);
    setCursor(s.cursor);
    setFocusPath(s.focusPath);
    onChange?.(s.tokens);
  }, [onChange]);

  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    if (stack.length === 0) {
      log.debug("history", "undo — stack empty");
      return;
    }
    const prev = stack[stack.length - 1];
    redoStackRef.current = [...redoStackRef.current, takeSnapshot()].slice(-UNDO_STACK_LIMIT);
    undoStackRef.current = stack.slice(0, -1);
    log.info("history", "undo", { undoDepth: undoStackRef.current.length, redoDepth: redoStackRef.current.length });
    applySnapshot(prev);
  }, [takeSnapshot, applySnapshot]);

  const redo = useCallback(() => {
    const stack = redoStackRef.current;
    if (stack.length === 0) {
      log.debug("history", "redo — stack empty");
      return;
    }
    const next = stack[stack.length - 1];
    undoStackRef.current = [...undoStackRef.current, takeSnapshot()].slice(-UNDO_STACK_LIMIT);
    redoStackRef.current = stack.slice(0, -1);
    log.info("history", "redo", { undoDepth: undoStackRef.current.length, redoDepth: redoStackRef.current.length });
    applySnapshot(next);
  }, [takeSnapshot, applySnapshot]);

  /**
   * Library modal state — Load-from-library + Save-to-library. The
   * load modal hands back tokens (preferred) or LaTeX-only formulas;
   * if tokens are present they replace the editor's state entirely.
   */
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const handleLoadFromLibrary = useCallback((
    payload: { formula: { latexContent: string; name?: string }; tokens: Token[] | null },
  ) => {
    pushHistory();
    if (payload.tokens && payload.tokens.length > 0) {
      log.info("library", "loaded with tokens", {
        name: payload.formula.name, tokenCount: payload.tokens.length,
      });
      setTokens(payload.tokens);
      setCursor(payload.tokens.length);
      onChange?.(payload.tokens);
    } else {
      // No tokens stored — try the minimal LaTeX → tokens parser so
      // the visual editor lands in a usable state. If the parser hit
      // anything it doesn't model yet, we keep the parsed prefix +
      // jump to the Source tab so the user can finish editing the
      // raw LaTeX themselves.
      const parsed = latexToTokens(payload.formula.latexContent);
      log.info("library", parsed.ok ? "parsed LaTeX → tokens" : "parsed LaTeX (partial)", {
        name: payload.formula.name,
        latex: payload.formula.latexContent,
        tokenCount: parsed.tokens.length,
        unparsed: parsed.unparsed,
      });
      const next = parsed.tokens.length > 0
        ? parsed.tokens
        : [T.letter(payload.formula.latexContent)];
      setTokens(next);
      setCursor(next.length);
      if (!parsed.ok) setDrawerTab("source");
      onChange?.(next);
    }
    setFocusPath([]);
    setSlashOpen(false);
  }, [onChange]);

  // Apply raw LaTeX (from the editable Source tab or the AI box) → tokens.
  // Best-effort parse; if nothing parses, keep the raw string as a single
  // letter token so the user's input isn't lost (mirrors the library path).
  const applyLatex = useCallback((latex: string) => {
    const parsed = latexToTokens(latex);
    const next = parsed.tokens.length > 0 ? parsed.tokens : (latex ? [T.letter(latex)] : []);
    setTokens(next);
    setCursor(next.length);
    setFocusPath([]);
    setSlashOpen(false);
    onChange?.(next);
  }, [onChange]);

  /**
   * Autosave + explicit-save state. The dropdown surfaces only when
   * a parent supplied `onSaveToServer`. Otherwise the editor is in
   * "Done-only" mode (legacy callers that don't wire server save).
   */
  const [autosaveSeconds, setAutosaveSeconds] = useState<number>(() => loadAutosaveSeconds());
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [lastAutosaveAt, setLastAutosaveAt] = useState<number | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  // `latestTokensRef` (declared with the undo/redo refs above) also
  // backs the autosave timer below — it reads fresh tokens without
  // forcing every keystroke to reset the interval.

  const handleSaveToServer = useCallback(async (trigger: "manual" | "autosave") => {
    if (!onSaveToServer) {
      log.debug("save", "no host save handler — skipping", { trigger });
      return;
    }
    if (saving) {
      log.debug("save", "save already in flight — skipping", { trigger });
      return;
    }
    const latex = equationToLatex(latestTokensRef.current);
    log.info("save", `starting ${trigger} save`, { latex, tokens: latestTokensRef.current.length });
    setSaving(true);
    try {
      await onSaveToServer(latex);
      const ts = Date.now();
      setLastSavedAt(ts);
      if (trigger === "autosave") setLastAutosaveAt(ts);
      log.info("save", `${trigger} save succeeded`, { atIso: new Date(ts).toISOString() });
    } catch (e) {
      log.error("save", `${trigger} save failed`, { error: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }, [onSaveToServer, saving]);

  const onChangeAutosaveSeconds = useCallback((n: number) => {
    setAutosaveSeconds(n);
    saveAutosaveSeconds(n);
    log.info("autosave", `interval changed to ${n === 0 ? "off" : `${n}s`}`);
  }, []);

  useEffect(() => {
    if (!onSaveToServer || autosaveSeconds <= 0) return;
    log.debug("autosave", `scheduling tick every ${autosaveSeconds}s`);
    const id = window.setInterval(() => {
      log.debug("autosave", "tick fired");
      handleSaveToServer("autosave");
    }, autosaveSeconds * 1000);
    return () => {
      window.clearInterval(id);
      log.debug("autosave", "tick cleared");
    };
  }, [onSaveToServer, autosaveSeconds, handleSaveToServer]);

  const flashTimer = useRef<number | null>(null);
  // A flash still pending when the editor closes must not fire into an unmounted
  // component (it failed CI on 10 Oct: "window is not defined" after teardown).
  useEffect(
    () => () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    },
    [],
  );

  // Responsive: side drawer at ≥ 980; otherwise fold below. Below 720
  // is too narrow — we render the page but show a notice.
  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Side-drawer vs block-drawer is viewport-based for now. A future
  // refinement keys off the editor body's own width via ResizeObserver
  // (see HANDOFF.md — the standalone playground branch). Viewport is
  // good enough for the common path; the narrow-modal-on-wide-monitor
  // case still folds correctly because the new mode="embedded" strips
  // the page chrome that was crowding the modal.
  const useSideDrawer = viewport >= 980;
  const tooNarrow = viewport < TOO_NARROW_BREAKPOINT;

  // All edits operate on the "active container" — the token list
  // addressed by `focusPath` (the root list when the path is empty).
  // `cursor` indexes into that container. Reading state through refs
  // keeps these callbacks stable and free of stale-closure bugs.

  const insert = useCallback((token: Token) => {
    pushHistory();
    const path = latestFocusPathRef.current;
    const pos = latestCursorRef.current;
    log.debug("insert", `kind=${token.kind}`, {
      cursor: pos, depth: path.length,
      structKind: (token as { structKind?: string }).structKind,
    });
    setTokens((prev) => {
      const next = replaceContainer(prev, path, (arr) => {
        const a = [...arr];
        a.splice(pos, 0, token);
        return a;
      });
      onChange?.(next);
      return next;
    });
    setSlashOpen(false);
    // A new structure auto-focuses its first slot so typing fills it
    // immediately (descending one level into the tree). Atoms just
    // advance the caret past themselves.
    if (token.kind === "struct" && (token as StructToken).slotOrder.length > 0) {
      setFocusPath([...path, { tokenIdx: pos, slotKey: (token as StructToken).slotOrder[0] }]);
      setCursor(0);
    } else {
      setCursor(pos + 1);
    }
    // Flash only applies to the top-level row (the renderer flashes by
    // top-level index); skip it for nested inserts.
    if (path.length === 0) {
      setFlashKey(pos);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setFlashKey(null), 420);
    }
  }, [onChange, pushHistory]);

  /**
   * Insert a sub/sup script bound to the preceding atom. The atom right
   * before the caret is pulled into the script's base slot (so `a` `^`
   * `2` becomes `a^{2}` with no dangling empty base box); the chosen
   * script slot is focused for the user to type into.
   */
  const insertScript = useCallback((which: "sup" | "sub") => {
    pushHistory();
    const path = latestFocusPathRef.current;
    const pos = latestCursorRef.current;
    const container = getContainer(latestTokensRef.current, path);
    const prev = pos > 0 ? container[pos - 1] : undefined;
    const pullPrev = !!prev && prev.kind !== "newline" && prev.kind !== "align";
    const base: Token[] = pullPrev ? [prev as Token] : [];
    const insertPos = pullPrev ? pos - 1 : pos;
    const script = T.subsup(base, [], []);
    setTokens((prevTokens) => {
      const next = replaceContainer(prevTokens, path, (arr) => {
        const a = [...arr];
        if (pullPrev) a.splice(pos - 1, 1);
        a.splice(insertPos, 0, script);
        return a;
      });
      onChange?.(next);
      return next;
    });
    setFocusPath([...path, { tokenIdx: insertPos, slotKey: which }]);
    setCursor(0);
    setSlashOpen(false);
  }, [onChange, pushHistory]);

  const backspace = useCallback(() => {
    const path = latestFocusPathRef.current;
    const pos = latestCursorRef.current;
    if (pos === 0) {
      // At the start of a nested slot: exit to the parent, parking the
      // caret just after the structure we were inside. At the root start
      // there is nothing to do (skip recording an empty history step).
      if (path.length === 0) return;
      pushHistory();
      const last = path[path.length - 1];
      setFocusPath(path.slice(0, -1));
      setCursor(last.tokenIdx + 1);
      return;
    }
    pushHistory();
    setTokens((prev) => {
      const next = replaceContainer(prev, path, (arr) => {
        const a = [...arr];
        a.splice(pos - 1, 1);
        return a;
      });
      onChange?.(next);
      return next;
    });
    setCursor(pos - 1);
  }, [onChange, pushHistory]);

  const moveCaret = useCallback((i: number) => {
    // Caret clicks land in the top-level row.
    setFocusPath([]);
    setCursor(Math.max(0, Math.min(latestTokensRef.current.length, i)));
  }, []);

  /** Move the caret within the active container (arrow keys). */
  const moveCursorBy = useCallback((delta: number) => {
    const len = getContainer(latestTokensRef.current, latestFocusPathRef.current).length;
    setCursor((c) => Math.max(0, Math.min(len, c + delta)));
  }, []);

  /** Exit the current slot one level up (Escape). */
  const ascendFocus = useCallback(() => {
    const path = latestFocusPathRef.current;
    if (path.length === 0) return;
    const last = path[path.length - 1];
    setFocusPath(path.slice(0, -1));
    setCursor(last.tokenIdx + 1);
  }, []);

  /**
   * Tab between slots. Cycles within the current structure's slot order;
   * when it runs past the end, it pops up a level and cycles the parent
   * structure's slots (so Tab out of a deeply-nested slot lands on the
   * enclosing structure's next slot — e.g. radicand → fraction
   * denominator). Climbing out of the outermost structure exits slot
   * mode to the top-level caret.
   */
  const cycleSlot = useCallback((direction: 1 | -1): boolean => {
    const root = latestTokensRef.current;
    const start = latestFocusPathRef.current;
    if (start.length === 0) return false;
    let path = start;
    while (path.length > 0) {
      const parent = path.slice(0, -1);
      const last = path[path.length - 1];
      const container = getContainer(root, parent);
      const st = container[last.tokenIdx];
      if (!st || st.kind !== "struct") return false;
      const order = (st as StructToken).slotOrder;
      const nextIdx = order.indexOf(last.slotKey) + direction;
      if (nextIdx >= 0 && nextIdx < order.length) {
        const newPath = [...parent, { tokenIdx: last.tokenIdx, slotKey: order[nextIdx] }];
        setFocusPath(newPath);
        // Forward → caret at slot start; backward → caret at slot end.
        setCursor(direction === 1 ? 0 : getContainer(root, newPath).length);
        return true;
      }
      path = parent;
    }
    // Climbed out of the outermost structure — exit slot mode.
    const outer = start[0];
    setFocusPath([]);
    setCursor(direction === 1 ? outer.tokenIdx + 1 : outer.tokenIdx);
    return true;
  }, []);

  const onSlotClick = useCallback((path: FocusPath) => {
    // Focus the clicked slot; park the caret at the end of its content.
    setFocusPath(path);
    setCursor(getContainer(latestTokensRef.current, path).length);
  }, []);

  const pushRecent = useCallback((entry: SymbolEntry) => {
    setRecent((prev) => {
      const filtered = prev.filter((r) => r.c !== entry.c);
      return [entry, ...filtered].slice(0, 8);
    });
  }, []);

  // Wrap insert to also push to recents when the token has a `cat`/glyph.
  const insertAndRemember = useCallback((token: Token) => {
    if (token.kind !== "struct" && token.kind !== "newline" && token.kind !== "align" && token.g) {
      pushRecent({ g: token.g, c: token.g, n: token.g });
    }
    insert(token);
  }, [insert, pushRecent]);

  /** Top-level keyboard handler. Mirrors the design handoff. */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // `e.target` on a window-level listener is usually `window` (no
      // .matches). Be defensive: only call .matches when it's an
      // Element. This avoids a crash when the page hasn't focused
      // anything in particular yet (regression spotted by the
      // MathEditor.test.tsx keyboard suite).
      const target = e.target;
      const inField =
        target instanceof Element &&
        target.matches("input, textarea, [contenteditable='true']");
      if (inField) return;

      // While the cheat sheet is open, swallow keys so the equation behind it
      // isn't edited. (The CheatSheet owns Escape / `?` to close it.)
      if (cheatOpenRef.current) return;

      // Undo / redo (#71). Handled BEFORE the modifier early-return
      // below, which otherwise swallows every Cmd/Ctrl-chorded key.
      // Cmd/Ctrl+Z undo, Cmd/Ctrl+Shift+Z or Cmd/Ctrl+Y redo.
      if ((e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        redo();
        return;
      }

      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // `?` opens the shortcut cheat sheet (Shift+/ — no other modifiers).
      if (e.key === "?") { e.preventDefault(); setCheatOpen(true); return; }

      // Unified editing model: every edit targets the "active container"
      // (root list or focused slot) via the focus path; there is no
      // separate slot-typing mode. Typing, symbols, and structures all
      // land in the focused slot when one is focused.
      const inSlot = latestFocusPathRef.current.length > 0;

      if (e.key === "Escape") {
        // Exit the current slot one level up; ignored at the root.
        if (inSlot) { e.preventDefault(); ascendFocus(); }
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        if (inSlot) {
          cycleSlot(e.shiftKey ? -1 : 1);
        } else {
          const tabs: DrawerTab[] = ["symbols", "structures", "source"];
          setDrawerTab((d) => tabs[(tabs.indexOf(d) + 1) % tabs.length]);
        }
        return;
      }
      if (e.key === "Backspace") { e.preventDefault(); backspace(); return; }
      if (e.key === "ArrowLeft")  { e.preventDefault(); moveCursorBy(-1); return; }
      if (e.key === "ArrowRight") { e.preventDefault(); moveCursorBy(1); return; }
      // newline / align only make sense in the top-level multi-line grid.
      if (!inSlot && e.key === "Enter" && e.shiftKey) { e.preventDefault(); insert(T.newline()); return; }
      if (!inSlot && e.key === "&") { e.preventDefault(); insert(T.align()); return; }
      // `\` opens the command palette. `/` is NOT a trigger — it types a
      // literal division slash (the palette is still reachable via `\`).
      if (e.key === "\\") { e.preventDefault(); setSlashOpen(true); return; }
      if (e.key === "^" || e.key === "_") {
        // Script the preceding atom: `a` `^` `2` → `a^{2}`, no dangling
        // empty base slot. Works at any nesting depth.
        e.preventDefault();
        insertScript(e.key === "^" ? "sup" : "sub");
        return;
      }
      if (e.key === "=") { e.preventDefault(); insert(T.rel("=")); return; }
      if (e.key === "+") { e.preventDefault(); insert(T.op("+")); return; }
      if (e.key === "-") { e.preventDefault(); insert(T.op("−")); return; }
      if (e.key === "*") { e.preventDefault(); insert(T.op("×")); return; }
      if (e.key === "<" || e.key === ">") { e.preventDefault(); insert(T.rel(e.key)); return; }
      if (/^[a-zA-Z]$/.test(e.key)) {
        e.preventDefault();
        const isUp = e.key === e.key.toUpperCase();
        insert(isUp ? T.upletter(e.key) : T.letter(e.key));
        return;
      }
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        insert(T.num(e.key));
        return;
      }
      // Punctuation / delimiters typed literally — parens, brackets,
      // commas etc. were previously un-typeable (and aren't in the symbol
      // palette), forcing users to hunt for them. Insert as punct atoms.
      if (/^[(),.;:[\]!?'|/]$/.test(e.key)) {
        e.preventDefault();
        insert(T.punct(e.key));
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [insert, insertScript, backspace, cycleSlot, ascendFocus, moveCursorBy, undo, redo]);

  const latexBody = useMemo(() => equationToLatex(tokens), [tokens]);
  const tokenCount = tokens.length;

  if (tooNarrow) {
    return (
      <div style={{
        padding: 40, textAlign: "center",
        background: "var(--lilia-bg-muted)",
        color: "var(--lilia-text-secondary)",
        fontFamily: "var(--lilia-font-sans, system-ui, sans-serif)",
      }}>
        <p style={{ fontSize: 16, fontWeight: 600, color: "var(--lilia-text)" }}>
          Lilia Math is desktop &amp; tablet only
        </p>
        <p style={{ fontSize: 13, marginTop: 8 }}>
          Open this editor on a screen at least 720px wide to start working.
        </p>
      </div>
    );
  }

  return (
    <div
      className="lilia-math-editor"
      data-testid="math-editor"
      data-token-count={tokens.length}
      data-cursor={cursor}
      data-saving={saving ? "1" : "0"}
      data-autosave-seconds={autosaveSeconds}
      style={{
        minHeight: 0,
        // Standalone (the /math-playground surface) fills its flex-row parent
        // edge-to-edge; embedded (drawer/equation block) keeps its natural size.
        ...(mode === "standalone" ? { flex: 1, width: "100%", minWidth: 0 } : {}),
        display: "flex", flexDirection: "column",
        position: "relative", // anchor for the `?` cheat-sheet overlay
        background: "var(--lilia-bg)", // editor chrome — themed (§6.1);
                                       // only the equation paper stays light
      }}
    >
      <CheatSheet open={cheatOpen} onClose={() => setCheatOpen(false)} />
      {!cheatOpen && (
        <button
          type="button"
          data-testid="math-cheatsheet-open"
          onClick={() => setCheatOpen(true)}
          title="Keyboard shortcuts (?)"
          aria-label="Keyboard shortcuts"
          style={{
            position: "absolute", right: 10, bottom: 10, zIndex: 5,
            width: 26, height: 26, borderRadius: "50%",
            border: "1px solid var(--lilia-border)", background: "var(--lilia-surface)",
            color: "var(--lilia-text-muted)", fontSize: 13, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "var(--lilia-shadow, 0 1px 3px rgba(0,0,0,.12))",
          }}
        >
          ?
        </button>
      )}
      {/* Theming (Claude Design 2026-06-14, §6.1/§6.4): the chrome
          (header, drawer, tabs, toolbar) inherits the active theme — only
          the equation "paper" stays light. We pin --lilia-surface/-border
          to white so the paper card is always a light writing surface, and
          re-establish a light text context inside the paper (.mc-paper) so
          rendered math + sub-text stay dark on white in every theme. Fonts
          are pinned (not colours). Everything else resolves from the theme. */}
      <style>{`
        .lilia-math-editor {
          --lilia-font-serif: 'IBM Plex Serif', Georgia, serif;
          --lilia-font-sans: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
          --lilia-font-mono: ui-monospace, 'SF Mono', 'Menlo', monospace;
          color: var(--lilia-text);
        }
        /* The equation card follows the active theme (founder direction
           2026-06-14, overriding Claude Design §6.1's "paper always white"):
           in dark themes the card is dark and KaTeX renders light via the
           inherited --lilia-text. Nothing about the paper is pinned anymore. */
        .lilia-math-editor .katex-error { color: var(--lilia-error); }
        @keyframes mc-caret { 0%, 50% { opacity: 1 } 50.01%, 100% { opacity: 0 } }
        @keyframes mc-flash { 0% { background: color-mix(in srgb, var(--lilia-primary) 45%, transparent) } 100% { background: transparent } }
        .mc-flash { animation: mc-flash 380ms ease-out; border-radius: 2px; }
      `}</style>

      {mode === "standalone" && (
        <Header
          label={label}
          breadcrumb={breadcrumb}
          onDone={() => {
            log.info("done", "Done pressed — handing LaTeX to host", { latex: latexBody });
            onDone?.(latexBody);
          }}
          onOpenLibrary={() => setLibraryOpen(true)}
          onSaveToLibrary={() => setSaveOpen(true)}
          canServerSave={!!onSaveToServer}
          onServerSave={() => handleSaveToServer("manual")}
          saving={saving}
          autosaveSeconds={autosaveSeconds}
          onChangeAutosaveSeconds={onChangeAutosaveSeconds}
        />
      )}

      <div style={{ flex: 1, display: "flex", justifyContent: "center", padding: "20px 20px 16px", minHeight: 0 }}>
        <div
          style={{
            width: "100%",
            // Standalone (the /math-playground driver surface) fills the page;
            // embedded (drawer/equation block) keeps a readable centered cap.
            maxWidth: mode === "standalone" ? "none" : useSideDrawer ? 1280 : 780,
            display: "flex",
            gap: 0,
            alignItems: "flex-start",
          }}
        >
          {/* Document column */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <EquationSurface
              tokens={tokens}
              cursor={cursor}
              flashKey={flashKey}
              onCaretMove={moveCaret}
              backspace={backspace}
              latexBody={latexBody}
              tokenCount={tokenCount}
              blockColors={blockColors}
              label={label}
              blockIndex={blockIndex}
              slashOpen={slashOpen}
              onCloseSlash={() => setSlashOpen(false)}
              onInsert={insertAndRemember}
              focusPath={focusPath}
              onSlotClick={onSlotClick}
            />
            {!useSideDrawer && (
              <BlockDrawer
                tab={drawerTab}
                setTab={setDrawerTab}
                onInsert={insertAndRemember}
                onScript={insertScript}
                recent={recent}
                tokens={tokens}
                label={label}
                showShortcuts={showShortcuts}
                commonTheme={commonTheme}
                onChangeCommonTheme={onChangeCommonTheme}
                onApplyLatex={applyLatex}
              />
            )}
          </div>

          {useSideDrawer && (
            <SideDrawer
              tab={drawerTab}
              setTab={setDrawerTab}
              onInsert={insertAndRemember}
              onScript={insertScript}
              recent={recent}
              tokens={tokens}
              label={label}
              showShortcuts={showShortcuts}
              commonTheme={commonTheme}
              onChangeCommonTheme={onChangeCommonTheme}
              onApplyLatex={applyLatex}
            />
          )}
        </div>
      </div>

      {/* Conditional mount — the modals call useApiContext, which
          throws when no ApiProvider is in the tree (e.g. unit tests
          that render MathEditor in isolation). Mounting only on open
          keeps the host contract unchanged. */}
      {libraryOpen && (
        <LibraryModal
          open={libraryOpen}
          onClose={() => { setLibraryOpen(false); log.info("library", "modal closed"); }}
          defaultTheme={commonTheme}
          onLoad={handleLoadFromLibrary}
        />
      )}
      {saveOpen && (
        <SaveToLibraryModal
          open={saveOpen}
          onClose={() => { setSaveOpen(false); log.info("library", "save-to-library modal closed"); }}
          tokens={tokens}
          defaultTheme={commonTheme}
        />
      )}

      {/* DevTools panel — only mounted when the localStorage flag is
          set at first render. Toggle in DevTools console:
            localStorage.setItem('lilia.math.devtools', '1'); location.reload()
          The panel reads through the props passed here; it does not
          subscribe to a context so it can't trigger re-renders of
          unrelated parts of the editor. */}
      {devtoolsOn && (
        <DevToolsPanel
          state={{
            tokens, cursor, focusPath, commonTheme,
            libraryOpen, saveOpen, drawerTab,
            latex: latexBody,
            lastSavedAt, lastAutosaveAt, autosaveSeconds,
          }}
        />
      )}
    </div>
  );
}

function Header({
  label, breadcrumb, onDone, onOpenLibrary, onSaveToLibrary,
  canServerSave, onServerSave, saving,
  autosaveSeconds, onChangeAutosaveSeconds,
}: {
  label?: string; breadcrumb?: string; onDone: () => void;
  onOpenLibrary: () => void; onSaveToLibrary: () => void;
  /** When true, render Save (to server) + Autosave dropdown. */
  canServerSave: boolean;
  onServerSave: () => void;
  saving: boolean;
  autosaveSeconds: number;
  onChangeAutosaveSeconds: (n: number) => void;
}) {
  return (
    <div style={{
      flexShrink: 0,
      background: "var(--lilia-surface)",
      borderBottom: "1px solid var(--lilia-border)",
      padding: "0 24px",
      height: 56,
      display: "flex", alignItems: "center", gap: 14,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 7,
        background: "linear-gradient(135deg, var(--lilia-primary) 0%, var(--lilia-primary-deep) 100%)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "#fff",
        fontFamily: "var(--lilia-font-serif, Georgia, serif)",
        fontStyle: "italic", fontSize: 16, fontWeight: 600,
      }}>L</div>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span style={{
          fontFamily: "var(--lilia-font-serif, Georgia, serif)",
          fontSize: 15, fontWeight: 600,
          color: "var(--lilia-text)",
        }}>Lilia Math</span>
        <span style={{ fontSize: 10, color: "var(--lilia-text-muted)", letterSpacing: "0.04em" }}>
          Equation editor · Beta
        </span>
      </div>

      <span style={{
        marginLeft: 10, padding: "3px 8px", borderRadius: 999,
        background: "color-mix(in srgb, var(--lilia-primary) 10%, transparent)", color: "var(--lilia-primary)",
        fontSize: 10, fontWeight: 600, letterSpacing: "0.04em",
      }}>EDITING</span>

      {(breadcrumb || label) && (
        <span style={{ fontSize: 12, color: "var(--lilia-text-muted)" }}>
          {breadcrumb && <span>{breadcrumb}</span>}
          {breadcrumb && label && " · "}
          {label && (
            <span style={{
              fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
              color: "var(--lilia-text-secondary)",
            }}>{label}</span>
          )}
        </span>
      )}

      <div style={{ flex: 1 }} />

      <span style={{
        height: 28, padding: "0 9px", display: "inline-flex", alignItems: "center", gap: 6,
        background: "var(--lilia-bg-muted)",
        border: "1px solid var(--lilia-border)",
        borderRadius: 6, fontSize: 11, color: "var(--lilia-text-muted)",
      }}>
        Type to insert · <KbdMini>\</KbdMini> command palette
      </span>
      {/* Load formula from the library — opens the LibraryModal which
          replaces the editor's current state on pick. */}
      <button
        type="button"
        onClick={onOpenLibrary}
        title="Load a formula from your library"
        data-testid="math-header-library"
        style={{
          height: 28, padding: "0 12px", borderRadius: 6,
          border: "1px solid var(--lilia-border)",
          background: "var(--lilia-surface)",
          color: "var(--lilia-text)",
          fontSize: 11.5, fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Library
      </button>
      <button
        type="button"
        onClick={onSaveToLibrary}
        title="Save the current equation to your library"
        data-testid="math-header-save-to-library"
        style={{
          height: 28, padding: "0 12px", borderRadius: 6,
          border: "1px solid var(--lilia-border)",
          background: "var(--lilia-surface)",
          color: "var(--lilia-text-secondary)",
          fontSize: 11.5, fontWeight: 600,
          cursor: "pointer",
        }}
      >
        → Library
      </button>
      {/* Explicit server-side save + autosave selector. Only shown
          when the host wired up `onSaveToServer`; legacy callers
          (and the standalone editor in dev) keep the Done-only flow. */}
      {canServerSave && (
        <>
          <button
            type="button"
            onClick={onServerSave}
            disabled={saving}
            title="Save the equation to the server now"
            data-testid="math-header-save"
            data-saving={saving ? "1" : "0"}
            style={{
              height: 28, padding: "0 14px", borderRadius: 6,
              border: 0, cursor: saving ? "wait" : "pointer",
              background: "var(--lilia-primary)",
              color: "#fff",
              fontSize: 11.5, fontWeight: 600,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <label
            title="How often the editor pushes to the server in the background"
            style={{
              height: 28, padding: "0 8px", borderRadius: 6,
              border: "1px solid var(--lilia-border)",
              background: "var(--lilia-surface)",
              color: "var(--lilia-text-secondary)",
              fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5,
              cursor: "pointer",
            }}
          >
            <span style={{ color: "var(--lilia-text-muted)" }}>Auto</span>
            <select
              value={autosaveSeconds}
              onChange={(e) => onChangeAutosaveSeconds(Number(e.target.value))}
              data-testid="math-header-autosave"
              style={{
                background: "transparent", border: 0, outline: "none",
                fontFamily: "inherit", fontSize: 11.5, fontWeight: 600,
                color: "var(--lilia-text)", cursor: "pointer",
              }}
            >
              <option value={0}>Off</option>
              <option value={30}>30s</option>
              <option value={60}>1m</option>
              <option value={120}>2m</option>
              <option value={300}>5m</option>
            </select>
          </label>
        </>
      )}
      <button
        type="button"
        onClick={onDone}
        data-testid="math-header-done"
        style={{
          height: 28, padding: "0 12px", borderRadius: 6, border: 0, cursor: "pointer",
          background: "var(--lilia-text)", color: "#fff",
          fontSize: 11.5, fontWeight: 600,
        }}
      >
        Done
      </button>
    </div>
  );
}

function EquationSurface({
  tokens, cursor, flashKey, onCaretMove, backspace, latexBody, tokenCount, blockColors,
  label, blockIndex, slashOpen, onCloseSlash, onInsert,
  focusPath, onSlotClick,
}: {
  tokens: Token[]; cursor: number; flashKey: number | null;
  onCaretMove: (i: number) => void; backspace: () => void;
  latexBody: string; tokenCount: number; blockColors: boolean;
  label?: string; blockIndex?: string;
  slashOpen: boolean; onCloseSlash: () => void;
  onInsert: (t: Token) => void;
  focusPath: FocusPath;
  onSlotClick: (path: FocusPath) => void;
}) {
  const focusedSlotKey = focusPath.length > 0 ? focusPath[focusPath.length - 1].slotKey : null;
  return (
    <div style={{ position: "relative", paddingTop: 12 }}>
      <div style={{
        position: "relative",
        background: "var(--lilia-surface)",
        border: "1.5px solid var(--lilia-primary)",
        borderRadius: 10,
        padding: "24px 22px 18px",
        boxShadow: "0 14px 36px color-mix(in srgb, var(--lilia-primary) 14%, transparent), 0 2px 6px rgba(0,0,0,0.04)",
      }}>
        {/* Block label tab overlapping the border */}
        <span style={{
          position: "absolute", top: -11, left: 14,
          background: "var(--lilia-primary)", color: "#fff",
          padding: "2px 8px", borderRadius: 999,
          fontSize: 10, fontWeight: 700,
          letterSpacing: "0.07em", textTransform: "uppercase",
        }}>
          Equation{label ? ` · ${label}` : ""}{blockIndex ? ` · ${blockIndex}` : ""}
        </span>

        {/* Render area — scrolls inside the block for very wide / tall equations. */}
        <div data-testid="math-equation-surface" style={{
          minHeight: 64, maxHeight: 360,
          overflow: "auto",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "10px 4px",
        }}>
          <EquationRenderer
            tokens={tokens}
            cursor={cursor}
            onCaretMove={onCaretMove}
            displaySize={32}
            flashKey={flashKey}
            blockColors={blockColors}
            focusPath={focusPath}
            onSlotClick={onSlotClick}
          />
        </div>

        {/* Slot-focus hint — visible whenever a structure slot is
            active, so users immediately understand what typing will
            do. Lives above the LaTeX preview so it doesn't fight
            with the token count / pos affordances. */}
        {focusedSlotKey && (
          <div style={{
            marginTop: 10, padding: "6px 10px",
            background: "color-mix(in srgb, var(--lilia-primary) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--lilia-primary) 30%, transparent)",
            borderRadius: 6,
            fontFamily: "var(--lilia-font-sans, system-ui, sans-serif)",
            fontSize: 11.5,
            color: "var(--lilia-text-secondary)",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{
              padding: "1px 6px",
              background: "var(--lilia-primary)",
              color: "#fff",
              borderRadius: 3,
              fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
            }}>
              SLOT
            </span>
            <span>
              Editing the <strong>{focusedSlotKey}</strong> slot — type to fill,
              <KbdInline>Tab</KbdInline> for the next slot,
              <KbdInline>Esc</KbdInline> to exit.
            </span>
          </div>
        )}

        {/* Footer row */}
        <div style={{
          marginTop: 12, paddingTop: 10,
          borderTop: "1px dashed var(--lilia-border)",
          display: "flex", alignItems: "center", gap: 12,
          fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
          fontSize: 11, color: "var(--lilia-text-muted)",
        }}>
          <span style={{
            flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            $ {latexBody.length > 80 ? latexBody.slice(0, 80) + "…" : latexBody} $
          </span>
          <span>{tokenCount} tokens</span>
          <span>pos {cursor}</span>
          <button
            type="button"
            onClick={backspace}
            style={{
              padding: "2px 8px", border: "1px solid var(--lilia-border)",
              borderRadius: 4, background: "transparent",
              fontFamily: "inherit", fontSize: 11,
              color: "var(--lilia-text-secondary)",
              cursor: "pointer",
            }}
          >
            ⌫ Delete
          </button>
        </div>
      </div>

      {slashOpen && (
        <SlashPalette onInsert={onInsert} onClose={onCloseSlash} />
      )}
    </div>
  );
}

interface DrawerCommonProps {
  tab: DrawerTab; setTab: (t: DrawerTab) => void;
  onInsert: (t: Token) => void;
  onScript: (which: "sup" | "sub") => void;
  recent: SymbolEntry[]; tokens: Token[]; label?: string;
  showShortcuts: boolean;
  commonTheme: ThemeId;
  onChangeCommonTheme: (t: ThemeId) => void;
  onApplyLatex?: (latex: string) => void;
}

function SideDrawer(p: DrawerCommonProps) {
  return (
    <aside style={{
      // Stretch to the parent flex container (which is the modal's
      // tab content area) rather than locking to viewport height —
      // a `100vh` value spilled past the modal's Save/Delete row and
      // hid both buttons when the editor was hosted inside
      // BlockEditModal. `alignSelf: stretch` makes the aside fill
      // the available height while the parent's `min-height: 0` lets
      // it shrink for smaller modals.
      alignSelf: "stretch",
      width: 460, flexShrink: 0,
      minHeight: 360,
      // Cap to the viewport (not just "100%", which resolves to unbounded
      // when the modal's flex chain doesn't propagate a definite height —
      // that made the whole modal scroll instead of the tab). 72vh keeps the
      // Symbols/Structures/Source grid scrolling *inside* the panel. (Founder
      // 2026-06-14: "the scroll for structures should be inside the tab".)
      maxHeight: "min(72vh, 100%)",
      background: "var(--lilia-surface)",
      border: "1px solid var(--lilia-border)",
      borderRadius: 10,
      boxShadow: "0 12px 28px rgba(0,0,0,0.04)",
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      <DrawerInner {...p} />
    </aside>
  );
}

function BlockDrawer(p: DrawerCommonProps) {
  return (
    <div style={{
      marginTop: 10,
      background: "var(--lilia-surface)",
      border: "1px solid var(--lilia-border)",
      borderRadius: 10,
      boxShadow: "0 6px 14px rgba(0,0,0,0.04)",
      maxHeight: 520,
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      <DrawerInner {...p} />
    </div>
  );
}

function DrawerInner({
  tab, setTab, onInsert, onScript, recent, tokens, label, showShortcuts,
  commonTheme, onChangeCommonTheme, onApplyLatex,
}: DrawerCommonProps) {
  return (
    <>
      <DrawerHeader tab={tab} setTab={setTab} />
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {tab === "symbols" && (
          <SymbolsTab
            onInsert={onInsert}
            recent={recent}
            showShortcuts={showShortcuts}
            commonTheme={commonTheme}
            onChangeCommonTheme={onChangeCommonTheme}
          />
        )}
        {tab === "structures" && <StructuresTab onInsert={onInsert} onScript={onScript} showShortcuts={showShortcuts} />}
        {tab === "source" && <SourceTab tokens={tokens} label={label} onApplyLatex={onApplyLatex} />}
      </div>
      <DrawerFooter />
    </>
  );
}

function DrawerHeader({ tab, setTab }: { tab: DrawerTab; setTab: (t: DrawerTab) => void }) {
  const tabs: Array<{ key: DrawerTab; label: string }> = [
    { key: "symbols", label: "Symbols" },
    { key: "structures", label: "Structures" },
    { key: "source", label: "Source" },
  ];
  return (
    <div style={{
      height: 36, display: "flex", alignItems: "stretch",
      borderBottom: "1px solid var(--lilia-border)",
      background: "var(--lilia-surface)",
    }}>
      {tabs.map((t) => {
        const active = t.key === tab;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`math-drawer-tab-${t.key}`}
            data-active={active ? "1" : "0"}
            style={{
              padding: "0 14px",
              border: 0, background: active ? "var(--lilia-bg-muted)" : "transparent",
              borderBottom: `2px solid ${active ? CAT_COLORS.greek.ink : "transparent"}`,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: active ? 600 : 500,
              color: active ? "var(--lilia-text)" : "var(--lilia-text-secondary)",
            }}
          >
            {t.label}
          </button>
        );
      })}
      <div style={{ flex: 1 }} />
      <span style={{
        alignSelf: "center", marginRight: 12,
        fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
        fontSize: 10, color: "var(--lilia-text-muted)",
      }}>
        <KbdMini>Tab</KbdMini> next tab
      </span>
    </div>
  );
}

function DrawerFooter() {
  return (
    <div style={{
      borderTop: "1px solid var(--lilia-border)",
      background: "var(--lilia-bg-muted)",
      padding: "6px 12px",
      display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8,
      fontSize: 10, color: "var(--lilia-text-muted)",
    }}>
      <KbdMini>\</KbdMini> command
      <KbdMini>^</KbdMini> sup
      <KbdMini>_</KbdMini> sub
      <KbdMini>/</KbdMini> frac
      <KbdMini>⇧↵</KbdMini> line
      <KbdMini>&amp;</KbdMini> align
      <span style={{ flex: 1 }} />
      <span>Click or type to insert at caret</span>
    </div>
  );
}

function KbdMini({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
      fontSize: 10, fontWeight: 600,
      background: "var(--lilia-surface)",
      border: "1px solid var(--lilia-border)",
      padding: "0 4px", borderRadius: 3,
      color: "var(--lilia-text-secondary)",
    }}>{children}</span>
  );
}

/** Inline kbd chip used in the slot-focus hint. */
function KbdInline({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      display: "inline-block",
      margin: "0 4px",
      padding: "0 5px",
      fontFamily: "var(--lilia-font-mono, ui-monospace, monospace)",
      fontSize: 10, fontWeight: 600,
      background: "var(--lilia-surface)",
      border: "1px solid color-mix(in srgb, var(--lilia-primary) 40%, transparent)",
      borderRadius: 3,
      color: "var(--lilia-primary)",
    }}>{children}</span>
  );
}
